import React, { ComponentType, useState, useRef } from 'react';
import URLS from 'URLS';
import { useUserState } from 'hooks/User';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAdmin } from 'apps/AdminPanel/hooks/useAdmin';
import { useAuth, useIsSchoolAuth } from 'hooks/Auth';

// Material UI
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Popper from '@mui/material/Popper';

// Project components
import Paper from 'components/layout/Paper';

// Icons
import AppsIcon from 'assets/icons/apps.svg?react';
import EventIcon from '@mui/icons-material/Event';
import ChatIcon from '@mui/icons-material/Chat';
import ChallengeIcon from '@mui/icons-material/EmojiEventsRounded';
import BandIcon from '@mui/icons-material/PlayArrowRounded';
import SettingsIcon from '@mui/icons-material/Settings';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AcademyIcon from '@mui/icons-material/School';
import GuitarIcon from 'apps/Academy/assets/img/guitar.svg?react';
import UkuleleIcon from 'apps/Academy/assets/img/ukulele.svg?react';
import ComposerIcon from 'assets/img/Composer.svg?react';
import PianoIcon from 'apps/Academy/assets/img/tempPiano.png';
import BassIcon from 'apps/Academy/assets/img/bass.svg?react';
import ChoirIcon from 'assets/img/ChoirIcon.svg?react';
import SingIcon from 'assets/img/SingIcon.svg?react';
import BoomWhackersIcon from '@mui/icons-material/BarChart';
import ResourcesIcon from '@mui/icons-material/LibraryBooks';

import { SvgIcon } from '@mui/material';
import { useTheme } from '@mui/material/styles';

const iconSx = { height: 26, width: 26 };

// Several of the app artwork SVGs carry `fill="#000000"` on their inner elements. MUI's
// `fill: currentColor` only lands on the root <svg>, and an inherited value loses to a
// presentation attribute, so those glyphs stayed black and vanished on the dark theme's
// surfaces. Repointing every explicit fill at currentColor makes them track the label
// colour in both themes; `fill="none"` is left alone since it carves out negative space.
const assetIconSx = {
  ...iconSx,
  '& [fill]:not([fill="none"])': { fill: 'currentColor' },
};

function Apps({ light }: { light?: boolean }) {
  const { t } = useTranslation(['common']);
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const user = useUserState();
  const navigate = useNavigate();
  const [auth] = useAuth();
  const [admin] = useAdmin();
  const theme = useTheme();
  const [isSchoolAuth, _, schoolAuthDoc] = useIsSchoolAuth();
  const isSchoolStudent = schoolAuthDoc !== null ? schoolAuthDoc?.data().role === 'student' : false;

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current?.contains(event.target as HTMLElement)) {
      return;
    }
    setOpen(false);
  };

  type AppProps = {
    icon?: ComponentType<{ style?: React.CSSProperties }>;
    iconFunction?: () => JSX.Element;
    text: string;
    to: string;
    external?: boolean;
  };

  // The icon/label column lives in a wrapper rather than on the Button root. MUI v4 stacked
  // them inside the Button's `label` span, which v5+ no longer renders; stacking on the root
  // itself puts the root's own 1.75 line-height in the column as an empty line box, which
  // padded every tile from 60px to 84px tall and turned the grid rows nearly square.
  const App = ({ icon: Icon, iconFunction, text, to, external }: AppProps) => (
    <Button
      onClick={() => {
        if (external) {
          window.open(to);
        } else {
          navigate(to);
        }
      }}
      sx={{ minWidth: '106px' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {Icon ? <Icon style={iconSx} /> : iconFunction ? iconFunction() : null}
        <Typography sx={{ maxWidth: 90, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }} variant='subtitle2'>
          {text}
        </Typography>
      </Box>
    </Button>
  );

  return (
    <>
      <IconButton aria-controls={open ? 'menu-list-grow' : undefined} aria-haspopup='true' onClick={handleToggle} ref={anchorRef}>
        <AppsIcon
          style={{
            height: 26,
            width: 26,
            // apps.svg declares no fill of its own, so this is the only thing colouring it.
            // A hardcoded black left the grid invisible against the dark theme's top bar.
            fill: light ? 'white' : theme.palette.black.main,
          }}
        />
      </IconButton>
      {/* Apps is always mounted in a top bar, so the menu hangs downwards. Relying on
          Popper's flip instead leaves it off-screen above the viewport. */}
      <Popper anchorEl={anchorRef.current} disablePortal={true} open={open} placement='bottom-end' role={undefined} sx={{ zIndex: 1600 }} transition>
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: 'center top' }}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <Box
                  sx={(theme) => ({
                    display: 'grid',
                    gap: theme.spacing(1),
                    gridTemplateColumns: '1fr 1fr 1fr',
                    [theme.breakpoints.down('sm')]: {
                      gap: theme.spacing(0.5),
                      gridTemplateColumns: '1fr 1fr',
                    },
                  })}>
                  {isSchoolAuth !== true && <App icon={ChatIcon} text={t('CHATS.HEADER')} to={URLS.CHAT} />}
                  {import.meta.env.VITE_SHOW_CHALLENGE === 'TRUE' && isSchoolAuth !== true && (
                    <App icon={ChallengeIcon} text={t('APPS.CHALLENGE')} to={URLS.CHALLENGE} />
                  )}
                  {import.meta.env.VITE_SHOW_EVENTS === 'TRUE' && isSchoolAuth !== true && (
                    <App icon={EventIcon} text={t('APPS.EVENTS')} to={URLS.EVENTS_EVENTS} />
                  )}
                  {import.meta.env.VITE_SHOW_PLAYER === 'TRUE' && <App icon={BandIcon} text={t('APPS.BAND')} to={URLS.PLAYER} />}
                  {import.meta.env.VITE_SHOW_SINGPLAYER === 'TRUE' && (
                    <App
                      iconFunction={() => <SvgIcon component={SingIcon} sx={assetIconSx} viewBox={'0 0 576 515'} />}
                      text={t('APPS.SING')}
                      to={URLS.SINGPLAYER}
                    />
                  )}
                  {import.meta.env.VITE_SHOW_UKULELEPLAYER === 'TRUE' && (
                    <App
                      iconFunction={() => <SvgIcon component={UkuleleIcon} sx={assetIconSx} viewBox={'0 0 60 60'} />}
                      text={t('APPS.UKULELE')}
                      to={URLS.UKULELEPLAYER}
                    />
                  )}
                  {import.meta.env.VITE_SHOW_GUITARPLAYER === 'TRUE' && (
                    <App
                      iconFunction={() => <SvgIcon component={GuitarIcon} sx={assetIconSx} viewBox={'0 0 404 404'} />}
                      text={t('APPS.GUITAR')}
                      to={URLS.GUITARPLAYER}
                    />
                  )}
                  {import.meta.env.VITE_SHOW_BOOMWHACKERSPLAYER === 'TRUE' && (
                    <App icon={BoomWhackersIcon} text={t('APPS.BOOMWHACKERS')} to={URLS.BOOMWHACKERSPLAYER} />
                  )}
                  {import.meta.env.VITE_SHOW_BASSTABSPLAYER === 'TRUE' && (
                    <App
                      iconFunction={() => <SvgIcon component={BassIcon} sx={assetIconSx} viewBox={'0 0 612 612'} />}
                      text={t('APPS.BASSTABS')}
                      to={URLS.BASSTABSPLAYER}
                    />
                  )}{' '}
                  {import.meta.env.VITE_SHOW_WHITEPIANOPLAYER === 'TRUE' && (
                    <App iconFunction={() => <img src={PianoIcon} style={iconSx} />} text={t('APPS.WHITEPIANO')} to={URLS.WHITEPIANOPLAYER} />
                  )}
                  {import.meta.env.VITE_SHOW_COMPOSER === 'TRUE' && (
                    <App
                      iconFunction={() => <SvgIcon component={ComposerIcon} sx={assetIconSx} viewBox={'0 0 576 515'} />}
                      text={t('APPS.COMPOSER')}
                      to={URLS.COMPOSER_WITHOUT_PLAYBACK}
                    />
                  )}
                  {import.meta.env.VITE_SHOW_CHOIRPLAYER === 'TRUE' && (
                    <App
                      iconFunction={() => <SvgIcon component={ChoirIcon} sx={assetIconSx} viewBox={'0 0 576 515'} />}
                      text={t('APPS.CHOIR')}
                      to={URLS.CHOIRPLAYER}
                    />
                  )}
                  {import.meta.env.VITE_SHOW_RESOURCES === 'TRUE' && <App icon={ResourcesIcon} text={t('APPS.RESOURCES')} to={URLS.RESOURCES} />}
                  {import.meta.env.VITE_SHOW_ACADEMY === 'TRUE' && <App icon={AcademyIcon} text={t('APPS.ACADEMY')} to={URLS.ACADEMY} />}
                  {import.meta.env.VITE_SHOW_ADMINPANEL === 'TRUE' && isSchoolAuth !== true && (admin?.player || admin?.events || admin?.license) && (
                    <App
                      icon={SettingsIcon}
                      text={t('APPS.ADMINPANEL')}
                      to={
                        admin?.player
                          ? URLS.ADMINPANEL_PLAYER
                          : admin?.events
                            ? URLS.ADMINPANEL_EVENTS
                            : admin?.license
                              ? URLS.ADMINPANEL_SCHOOLONBOARDING
                              : URLS.LANDING
                      }
                    />
                  )}
                  {isSchoolStudent !== true && <App external icon={ShoppingCartIcon} text={t('APPS.SHOP')} to={URLS.SHOP} />}
                  {/*
                  This is temporary removed due to "about us" page not existing
                    <App external icon={InfoIcon} text={t('APPS.ORG')} to={URLS.ORG} />
                  */}
                </Box>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}

export default Apps;
